<script setup lang="ts">
import type { UIDialogInstance } from '../../types'
import type { TransitionProps, HTMLAttributes } from 'vue'
import { onUnmounted, onMounted, watch, computed, ref, provide, UIDIALOG_KEY, nextTick, toValue, useDevice, shallowRef, useAttrs, useDialogContext, useId, useNuxtApp, useDefaults, useScrollLock } from '#imports'
import type { Name as ThemeName } from '#theme'

export interface UIDialogProps {
    header?: string
    wide?: boolean
    showCloseIcon?: boolean
    showHeader?: boolean
    showFooter?: boolean
    alignCenter?: boolean
    closeOnBackdropClick?: boolean
    scrollable?: boolean
    maxWidth?: string
    minHeight?: string
    maxHeight?: string
    headerLogoSrc?: string
    transitionName?: 'fade' | 'bounce-down'
    backdropProps?: {
        class?: HTMLAttributes['class']
        style?: HTMLAttributes['style']
        onClick?: (event: MouseEvent) => void
    }
    variant?: 'dialog' | 'promotion'
    theme?: ThemeName
}
const _props = withDefaults(defineProps<UIDialogProps>(), {
    header: undefined,
    wide: false,
    showCloseIcon: true,
    showHeader: true,
    showFooter: true,
    alignCenter: false,
    closeOnBackdropClick: true,
    scrollable: true,
    maxWidth: undefined,
    minHeight: '200px',
    maxHeight: undefined,
    transitionName: 'fade',
    headerLogoSrc: undefined,
    backdropProps: undefined,
    variant: 'dialog',
})

const props = useDefaults({
    props: _props
})

const emit = defineEmits<{
    'open': []
    'opened': []
    'close': []
    'closed': []
    'backdrop-click': []
}>()

const slots = defineSlots<{
    header?(props: UIDialogInstance): any
    default(props: UIDialogInstance): any
    footer?(props: UIDialogInstance): any
}>()

defineOptions({
    inheritAttrs: false
})

const dialogId = useId()
const attrs = useAttrs()
const { isMobile } = useDevice()
const dialogContext = useDialogContext(dialogId)
const { isHydrating } = useNuxtApp()

const isScrollLocked = import.meta.client ? useScrollLock(document.body) : ref(false)

watch(dialogContext.hasOpenDialogs, (open) => {
    isScrollLocked.value = open
}, { immediate: true })

const modelValue = defineModel<boolean>({ default: false })
const internalValue = ref<boolean>(modelValue.value)

const backdrop = ref<HTMLDivElement>()
const dialog = ref<HTMLDivElement>()

let isDestroying = false
// The bounce-down enter is still running; it emits `opened` itself once it lands.
const isEntering = shallowRef(false)
// The bounce-down exit is running. Tracked here because WAAPI only applies a reverse() to
// animation.playbackRate once the animation is ready, a frame later.
const isExiting = shallowRef(false)
// Every exit and resume awaits the same `finished`; only the latest one may act once it settles.
const directionRun = shallowRef(0)

async function open() {
    if (import.meta.server) {
        dialogContext.open()
    }

    await nextTick()

    dialogContext.open()

    if (!modelValue.value) {
        modelValue.value = true
    }
    internalValue.value = true

    emit('open')

    await resumeInterruptedClose()
}

// Reopened while the bounce-down exit is still running: play it back the way it came. Otherwise the
// exit finishes, close() hides the dialog, and nothing opens it again because the model never changed.
async function resumeInterruptedClose() {
    const running = animations.value

    if (!running || !isExiting.value) return

    const isEnterPending = isEntering.value
    const run = ++directionRun.value
    isExiting.value = false

    for (const animation of running) {
        animation.reverse()
        animation.updatePlaybackRate(1)
    }

    try {
        await Promise.all(running.map(animation => animation.finished))
    }
    catch {
        // Cancelled by unmount teardown.
        return
    }

    // Closed again before it got back, or the interrupted enter emits `opened` when it lands.
    if (isEnterPending || run !== directionRun.value) return

    emit('opened')
}

function close() {
    // Skip the exit animation while tearing down: its WAAPI `finished` callbacks
    // never fire after unmount (cancel() rejects them), which would leave this
    // dialog in the shared state and keep the `dialog-open` html class stuck on.
    if (!isDestroying && props.transitionName === 'bounce-down') {
        if (animations.value) {
            runBounceDownAnimation(undefined, close)

            return
        }
    }

    dialogContext.close()

    if (modelValue.value) {
        // Prevents unwanted emits if close() was called on a destroy and the dialog was not visible
        emit('close')

        modelValue.value = false
    }

    internalValue.value = false
}

function onBackdropClick() {
    // Emitted regardless of closeOnBackdropClick so consumers can intercept
    emit('backdrop-click')

    if (!props.closeOnBackdropClick) {
        return
    }

    close()
}

function toggle() {
    if (!modelValue.value) {
        return open()
    }

    return close()
}

/**
 * Manages the updating of `internalValue`, which lets us take full control of the animation flow
 */
function onModelValueChange() {
    if (modelValue.value) {
        open()
    }
    else {
        close()
    }
}

// Animations — the bounce-down enter/exit, ported from the old timeline to the
// Web Animations API: the backdrop darkens 0–400ms, the dialog flies in
// 400–1000ms; close reverses the whole thing at 1.5x.
const BACKDROP_DURATION_MS = 400
const DIALOG_DURATION_MS = 600
const EASE_OUT_QUAD = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' // $easeOutQuad
const EASE_OUT_QUART = 'cubic-bezier(0.165, 0.84, 0.44, 1)' // $easeOutQuart
const EASE_OUT_BACK_SOFT = 'cubic-bezier(0.175, 0.885, 0.32, 1.175)' // softened $easeOutBack, ~6% overshoot

const animations = shallowRef<Animation[]>()

const transitionProps = computed<TransitionProps>(() => {
    const value: TransitionProps = {
        appear: import.meta.client && !isHydrating,
        onAfterEnter: () => emit('opened'),
        onAfterAppear: () => emit('opened'),
        onAfterLeave: () => emit('closed')
    }

    if (props.transitionName === 'fade') {
        value.name = 'fade'
    }

    if (props.transitionName === 'bounce-down') {
        value.onEnter = runBounceDownAnimation
        value.css = false
    }

    return value
})

async function runBounceDownAnimation(el?: Element, done?: () => void) {
    const backdropRaw = (el ?? toValue(backdrop)) as HTMLElement | undefined
    const dialogRaw = toValue(dialog)

    // No WAAPI (SSR / older UA) → degrade to an instant open.
    if (!backdropRaw || !dialogRaw || typeof dialogRaw.animate !== 'function') {
        done?.()

        return
    }

    if (!animations.value) {
        // Enter: darken the backdrop, then fly the dialog in.
        const created = [
            // endDelay stretches the effect to the full 1000ms so a reverse from the
            // open state fades the backdrop out only after the dialog has flown out.
            backdropRaw.animate(
                { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
                { duration: BACKDROP_DURATION_MS, endDelay: DIALOG_DURATION_MS, easing: EASE_OUT_QUAD, fill: 'both' }
            ),
            // A single keyframe = implicit "from": the computed .dialog--bounce-down
            // start state (-100vh desktop / +100vh mobile) — no breakpoint duplication.
            dialogRaw.animate(
                { transform: 'translate(0, 0)', opacity: 1 },
                { delay: BACKDROP_DURATION_MS, duration: DIALOG_DURATION_MS, easing: isMobile ? EASE_OUT_QUART : EASE_OUT_BACK_SOFT, fill: 'both' }
            )
        ]
        animations.value = created
        isEntering.value = true

        try {
            await Promise.all(created.map(animation => animation.finished))
        }
        catch {
            // cancel() on unmount rejects `finished`; teardown close() cleans up.
            return
        }
        finally {
            isEntering.value = false
        }

        // `finished` also resolves at the start edge after a mid-flight reverse —
        // only complete the *enter* if we actually ran forward.
        if (isExiting.value) {
            return
        }

        done?.() // → Vue afterEnter → emit('opened')

        return
    }

    // Close: reverse the running (or finished) enter animations at 1.5x.
    const running = animations.value

    // reverse() TOGGLES direction — a second close() must not flip us forward again.
    if (isExiting.value) {
        return
    }

    const run = ++directionRun.value
    isExiting.value = true

    try {
        for (const animation of running) {
            animation.updatePlaybackRate(1.5)
            animation.reverse()
        }
        await Promise.all(running.map(animation => animation.finished))
    }
    catch {
        // Cancelled by unmount teardown.
        return
    }

    // Reopened mid-exit: resumeInterruptedClose() has turned it around, so this close no longer applies.
    if (run !== directionRun.value) return

    isExiting.value = false
    animations.value = undefined

    // Wait until nextTick, to make sure router state is correct etc.
    await nextTick()
    done?.()
}

defineExpose({
    open,
    close,
    toggle
})

provide(UIDIALOG_KEY, {
    open, close, toggle
})

if (import.meta.server) {
    onModelValueChange()
}

// Re-register with the client id on mount so the entry survives the ssr-entry cleanup that fires on
// app:suspense:resolve. The server id can differ from the client id (FRO-560).
if (import.meta.client && isHydrating) {
    onMounted(() => {
        if (internalValue.value) {
            dialogContext.open()
        }
    })
}

if (import.meta.client && !isHydrating) {
    onMounted(() => {
        onModelValueChange()
    })
}

onUnmounted(() => {
    isDestroying = true
    animations.value?.forEach(animation => animation.cancel())
    animations.value = undefined

    close()
})

watch(modelValue, () => {
    onModelValueChange()
})
</script>

<template>
    <Teleport to="#teleports">
        <ThemeProvider :theme="props.theme">
            <Transition v-bind="transitionProps">
                <div
                    v-if="internalValue"
                    v-bind="backdropProps"
                    ref="backdrop"
                    class="dialog__backdrop"
                    :class="{
                        'dialog__backdrop--center': alignCenter,
                        'dialog__backdrop--bounce-down': props.transitionName === 'bounce-down'
                    }"
                    role="dialog"
                    aria-modal="true"
                    data-test="ui-dialog"
                    @click.self="onBackdropClick"
                >
                    <div
                        ref="dialog"
                        class="dialog"
                        tabindex="-1"
                        :class="{
                            'dialog--scrollable': scrollable,
                            'dialog--wide': wide,
                            'dialog--has-max-height': !!maxHeight,
                            'dialog--promotion': variant === 'promotion',
                            'dialog--bounce-down': transitionName === 'bounce-down',
                        }"
                        :style="{
                            minHeight,
                            maxHeight,
                            maxWidth
                        }"
                        v-bind="attrs"
                    >
                        <div class="dialog__content">
                            <div v-if="showHeader" class="dialog__header">
                                <div class="dialog__header-bottom">
                                    <slot
                                        name="header"
                                        v-bind="{
                                            open,
                                            close,
                                            toggle
                                        }"
                                    >
                                        <UIToolbar height="60px">
                                            <template #left>
                                                <NuxtImg
                                                    v-if="headerLogoSrc"
                                                    :src="headerLogoSrc"
                                                    height="34"
                                                    quality="100"
                                                    preset="local"
                                                    provider="cloudflare"
                                                    decoding="async"
                                                    loading="lazy"
                                                    alt="Site logo"
                                                    class="dialog__logo"
                                                />
                                            </template>

                                            <template #center>
                                                <h2 v-if="header" v-text="header" />
                                            </template>

                                            <template #right>
                                                <!-- An iconless close button is an invisible dismiss. -->
                                                <UIButton
                                                    v-if="showCloseIcon"
                                                    color="link"
                                                    icon
                                                    size="sm"
                                                    @click="close"
                                                >
                                                    <UIIcon
                                                        name="close"
                                                        size="16"
                                                        class="dialog__close"
                                                    />
                                                </UIButton>
                                            </template>
                                        </UIToolbar>
                                    </slot>
                                </div>
                            </div>

                            <div
                                class="dialog__body"
                            >
                                <slot v-bind="{ open, close, toggle }" />
                            </div>

                            <div v-if="showFooter && slots.footer" class="dialog__footer">
                                <slot name="footer" v-bind="{ open, close, toggle }" />
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </ThemeProvider>
    </Teleport>
</template>

<style lang="scss" scoped>
.dialog {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: var(--background, $dialog-bg);
    color: var(--on-surface, $dialog-font-color);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    user-select: none;
    pointer-events: none;

    @media (min-width: $screen-sm-md) {
        display: flex;
        flex: 1;
        flex-direction: column;
        width: 414px;
        height: min-content;
        max-width: 414px;
        border-radius: var(--border-radius, $border-radius);
        margin: auto;
    }

    &__content {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        contain: layout;
        pointer-events: auto;
        min-height: inherit; // Inherit from parent minHeight prop

        @media (min-width: $screen-sm-md) {
            max-height: 90dvh;
        }
    }

    &__wrapper {
        display: flex;
        flex: 1;
    }

    &__backdrop {
        display: flex;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        justify-content: center;
        background: $backdrop;
        width: 100%;
        height: 100%;
        z-index: z(dialog);
        user-select: none;
        pointer-events: auto;

        &--center {
            align-items: center;

            // Prevents long texts going out of frame
            .dialog {
                max-height: 100%;
            }
        }

        &--bounce-down {
            background: transparent;
        }
    }

    &__header {
        position: sticky;
        top: 0;
        background: var(--background, $dialog-header-bg);
        z-index: 10;

        h2 {
            margin: 0 auto 0 0;
            font-size: $font-size-base;
            font-weight: $text-heavy;
            text-align: center;
            text-transform: uppercase;

            @media (min-width: $screen-sm) {
                font-size: $font-size-lg;
            }
        }
    }

    &__footer {
        display: flex;
        flex-direction: column-reverse;
        width: 100%;
        padding: 0 30px 30px 30px;
        background: var(--background, $dialog-footer-bg);

        @media (min-width: $screen-sm-md) {
            flex-direction: row;
        }

        .btn {
            width: 100%;
            margin-top: 10px;

            @media (min-width: $screen-sm-md) {
                margin: 0 10px;

                &:first-child {
                    margin-left: 0;
                }

                &:last-child {
                    margin-right: 0;
                }
            }

            &--top-shadow {
                box-shadow: 0 -12px 50px 12px var(--background, #e9eaee);
            }
        }

        .btn-block {
            margin-top: 0 !important; // Must be !important to override Bootstrap
        }
    }

    &__logo {
        height: 34px;
        width: auto;
        margin-right: 8px;
    }

    &__body {
        display: block;
        flex-grow: 1;
        padding: 20px 30px;
        color: var(--on-surface, $dialog-font-color);
        font-size: $font-size-sm;
        user-select: text;

        // Prevents large indent of terms in dialogs
        ol,
        ul {
            padding-left: 15px;
        }
    }

    &__close {
        color: var(--on-surface, $close-btn-default);
        font-size: $font-size-base;

        .icon-sn-arrow-left {
            font-size: $font-size-xl;
        }

        &:hover {
            color: var(--on-surface-hover, $close-btn-hover);
        }
    }

    &--scrollable {
        .dialog__body {
            overflow-y: auto;
        }
    }

    &--wide {
        @media (min-width: $screen-sm-md) {
            max-width: 80%;
            margin: auto;
        }

        @media (min-width: $screen-lg) {
            max-width: $screen-md;
        }
    }

    &--has-max-height {
        @media (max-width: #{$screen-sm-md - 1}) {
            align-self: end;
        }
    }

    &--promotion {
        position: relative;
        color: var(--on-surface, $dialog-font-color);
        background: var(--surface, $promotion-dialog-bg);
        border-radius: var(--border-radius-lg, $border-radius-lg) var(--border-radius-lg, $border-radius-lg) 0 0;

        // WebKit resolves `min-content` on this bottom sheet to the min-height, which squeezed
        // tall promotions into a 250px box. `auto` sizes to the content in every engine.
        height: auto;
        max-height: 100dvh;
        align-self: flex-end;

        @media (min-width: $screen-sm-md) {
            border-radius: var(--border-radius-lg, $border-radius-lg);
            max-height: 90dvh;
        }

        .dialog {
            &__content {
                max-height: inherit;
            }

            &__footer,
            &__header {
                background: transparent;
            }

            &__header {
                position: absolute;
                top: 0;
                width: 100%;
            }
        }
    }

    &--bounce-down {
        opacity: 0;
        transform: translate(0, -100vh);

        @media (max-width: #{$screen-sm-md - 1}) {
            position: absolute;
            bottom: 0;

            height: auto;
            transform: translate(0, 100vh);
        }
    }
}
</style>
